
import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

import { GlobalService } from './global.service';

import { AXIOS, CommonGlobalService, UUID, CallbackErrorAxios, CallbackWarningAxios, dayjs } from '@umanforge/common';
import { ModifyRecurrence, Recurrence } from 'interfaces/recurrence.interface';
import { API } from 'constants/api.contants';


export class RecurrenceService {

  static getRecurrenceById(
    recurrenceId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Recurrence> {
    return new Promise( async ( resolve, reject ) => {
      AXIOS.get( `${API.RECURRENCE}/${recurrenceId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<Recurrence> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          reject( err );
        } );
    } );
  }

  static getRecurrenceByTaskId(
    taskId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Recurrence | undefined> {
    return new Promise( async ( resolve, reject ) => {
      AXIOS.get( `${API.RECURRENCE_BY_TASK}/${taskId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<Recurrence | undefined> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          reject( err );
        } );
    } );
  }

  static async putRecurrence(
    recurrenceId: string,
    recurrence: Partial<ModifyRecurrence>,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ) {
    type RecurrencePayload = Partial<ModifyRecurrence> & {
      idTask?: string;
      repeat_value?: number;
      repeat_type?: number;
    };

    const rawRecurrence = recurrence as RecurrencePayload;
    const currentRecurrence: Recurrence | undefined = await this.getRecurrenceById( recurrenceId, token, dispatch, onError, onWarning )
      .catch( ( error: unknown ) => {
        void error;
        return undefined;
      } );

    const payload: ModifyRecurrence = {
      ...recurrence,
      taskId: rawRecurrence.taskId ?? rawRecurrence.idTask ?? '',
      value: rawRecurrence.value ?? currentRecurrence?.value ?? 0,
      type: rawRecurrence.type ?? currentRecurrence?.type ?? 'daily',
      dateStart: rawRecurrence.dateStart ?? currentRecurrence?.dateStart ?? dayjs().toISOString(),
      dateEnd: rawRecurrence.dateEnd ?? currentRecurrence?.dateEnd ?? dayjs().toISOString(),
      repeat: rawRecurrence.repeat ?? rawRecurrence.repeat_value ?? currentRecurrence?.repeat ?? 1,
      repeatType: rawRecurrence.repeatType ?? rawRecurrence.repeat_type ?? currentRecurrence?.repeatType ?? 1,
    };
    delete ( payload as Partial<ModifyRecurrence> & { priority?: number } ).priority;

    if ( !payload.taskId ) {
      const error = new AxiosError( 'Recurrence PUT payload is missing required field: taskId' );
      GlobalService.onAxiosError( error, dispatch, onError );
      return;
    }

    return new Promise( async ( resolve, reject ) => {
      AXIOS.put( `${API.RECURRENCE}/${recurrenceId}`, payload, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          reject( err );
        } );
    } );
  }

  static postRecurrence(
    recurrence: Partial<Recurrence>,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<{ id: string }> {
    const payload = {
      idTask: recurrence.idTask ?? '',
      value: recurrence.value ?? 0,
      type: recurrence.type ?? 'daily',
      dateStart: recurrence.dateStart ?? dayjs().toISOString(),
      dateEnd: recurrence.dateEnd ?? dayjs().toISOString(),
      repeat: recurrence.repeat ?? 1,
      repeatType: recurrence.repeatType ?? 1,
    };

    if ( !payload.idTask ) {
      const error = new AxiosError( 'Recurrence POST payload is missing required field: idTask' );
      GlobalService.onAxiosError( error, dispatch, onError );
      return Promise.resolve( { id: '' } );
    }

    return new Promise( async ( resolve, reject ) => {
      AXIOS.post( API.RECURRENCE, payload, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<{ id: string }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          reject( err );
        } );
    } );
  }

  static deleteRecurrence(
    recurrenceId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<void> {
    return new Promise( async ( resolve, reject ) => {
      AXIOS.delete( `${API.RECURRENCE}/${recurrenceId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve();
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          reject( err );
        } );
    } );
  }
};